import React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { TenderTypes } from '@web-kc/core/api';
import { useTranslation } from 'next-i18next';
import { LotItem } from '../LotItem';
import Link from 'next/link';
import { useRouter } from 'next/router';

export const TenderList = ({
  data,
  isLoading,
}: {
  data?: TenderTypes['PageContentTenderDto'];
  isLoading: boolean;
}) => {
  const { pathname } = useRouter();
  const { t } = useTranslation(['cabinet', 'common']);
  const theme = useTheme();

  const isFavorites = pathname.startsWith('/cabinet/favorite');

  if (isLoading) return <CircularProgress sx={{ padding: '8px' }} />;

  if (!data?.content.length) {
    return (
      <Stack justifyContent="center" direction="row">
        <Typography variant="body2" fontWeight={700}>
          {t('common:common.notFound')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap={{ xs: 1, md: 2 }}>
      {data.content.map((tender) => (
        <Box
          key={tender.id}
          sx={{
            pb: 2.5,
            pt: { xs: 2.5, md: 1 },
            borderBottom: `1px solid ${theme.palette.grey['300']}`,
            '&:last-of-type': { borderBottom: 'none' },
            backgroundColor: { xs: 'white', md: 'inherit' },
            borderRadius: { xs: 2, md: 0 },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: `1px solid ${theme.palette.grey['300']}`,
              pb: 2.5,
              px: { xs: 2, md: 0 },
            }}
          >
            <Typography
              fontSize={18}
              fontWeight={700}
              component={Link}
              href={
                tender?.status?.id === 'DRAFT' ||
                tender?.status?.id === 'ON_REVISION'
                  ? `/cabinet/tenders/edit/${tender.id}`
                  : `/cabinet${
                      isFavorites ? '/favorite-tenders' : '/tenders'
                    }/${tender.id}`
              }
              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
            >
              {t('cabinet:myTenders.tender')} №{tender.id} {t('myTenders.of')}{' '}
              {tender.lotCount}{' '}
              {Number(tender.lotCount) > 1
                ? t('myTenders.lots2')
                : t('myTenders.lots1')}
            </Typography>
          </Stack>

          <Stack
            direction="column"
            gap={2.5}
            sx={{ paddingX: '20px', mt: '20px' }}
          >
            {tender?.lots?.map((lot) => (
              <LotItem key={lot.id} lot={lot} />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
